// ============================================================================
// ASSET VALUATION TAB COMPONENT
// Finance-facing depreciation and valuation editing for asset work items.
// Surfaces purchase price, depreciation method, useful life, salvage value,
// and computed current book value / depreciation schedule.
//
// Phase 6: Now also shows persisted depreciation run history from the server
// and provides actions to run depreciation, record impairment, and dispose
// of the asset — all via server-side GL-posted services.
// Date: August 2026
// ============================================================================

'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TrendingDown, DollarSign, Calendar, AlertTriangle, Save, Play, FileText, Trash2, AlertCircle } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { notify } from '@/lib/notifications';
import {
  calculateDepreciation,
  formatAssetCurrency,
  formatAssetDate,
  getAssetCurrencySymbol,
  toAssetNumber,
  generateDepreciationSchedule,
  generateTaxVsBookSchedule,
  type AssetCustomFields,
  type DepreciationMethod,
  type DepreciationResult,
  type DepreciationSchedule,
} from '@/components/shared/assetDepreciation';

// ============================================================================
// TYPES
// ============================================================================

interface DepreciationRun {
  id: number;
  depreciation_amount: number;
  accumulated_depreciation: number;
  book_value_after: number;
  method: string;
  journal_entry_id: number | null;
  run_date: string;
  posted_at: string | null;
  notes: string | null;
}

interface AssetValuationTabProps {
  /** The work item ID (UUID) — used for server-side depreciation/disposal/impairment calls. */
  workItemId: string;
  /** The custom_fields object from the work item (merged with formData) */
  customFields: Record<string, unknown>;
  /** Callback to update a custom_field key on the parent work item */
  onCustomFieldChange: (key: string, value: unknown) => void;
  /** Whether the parent is currently saving */
  isSaving?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function AssetValuationTab({
  workItemId,
  customFields,
  onCustomFieldChange,
  isSaving = false,
}: AssetValuationTabProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';

  const cf = customFields as AssetCustomFields;

  const purchasePrice = cf.purchase_price || toAssetNumber(cf.purchase_cost);
  const purchaseDate = cf.purchase_date || '';
  const depreciationMethod = cf.depreciation_method || 'none';
  const usefulLifeMonths = cf.useful_life_months || 0;
  const salvageValue = cf.salvage_value || 0;
  const supplier = cf.supplier || '';
  const warrantyExpiry = cf.warranty_expiry_date || '';
  const currency = cf.currency || 'ZAR';

  const depreciation: DepreciationResult | null = useMemo(
    () => calculateDepreciation(cf),
    [cf],
  );

  // Phase 13: Year-by-year depreciation schedule + tax vs book comparison
  const [showSchedule, setShowSchedule] = useState(false);
  const [showTaxConfig, setShowTaxConfig] = useState(false);

  const schedule: DepreciationSchedule | null = useMemo(
    () => generateDepreciationSchedule(cf),
    [cf],
  );

  const taxComparison = useMemo(
    () => generateTaxVsBookSchedule(cf),
    [cf],
  );

  // --- Phase 6: persisted run history + actions ---
  const [runHistory, setRunHistory] = useState<DepreciationRun[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [runningDep, setRunningDep] = useState(false);
  const [showImpairment, setShowImpairment] = useState(false);
  const [showDisposal, setShowDisposal] = useState(false);
  const [impairmentValue, setImpairmentValue] = useState('');
  const [impairmentReason, setImpairmentReason] = useState('');
  const [disposalDate, setDisposalDate] = useState(new Date().toISOString().split('T')[0]);
  const [disposalProceeds, setDisposalProceeds] = useState('');
  const [disposalReason, setDisposalReason] = useState('');
  const [postingImpairment, setPostingImpairment] = useState(false);
  const [postingDisposal, setPostingDisposal] = useState(false);

  const fetchRunHistory = useCallback(async () => {
    if (!workItemId) return;
    setLoadingHistory(true);
    try {
      const res = await fetch(`/api/v1/assets/depreciation-runs?asset_id=${workItemId}&limit=20`);
      const data = await res.json();
      if (data.success) setRunHistory(data.data || []);
    } catch {
      // Silently fail — history is informational
    } finally {
      setLoadingHistory(false);
    }
  }, [workItemId]);

  useEffect(() => {
    fetchRunHistory();
  }, [fetchRunHistory]);

  const handleRunDepreciation = useCallback(async () => {
    setRunningDep(true);
    try {
      const res = await fetch('/api/v1/assets/depreciation-runs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          asset_id: workItemId,
          units_produced: cf.units_produced,
        }),
      });
      const data = await res.json();
      if (data.success) {
        notify.success('Depreciation run posted successfully');
        fetchRunHistory();
      } else {
        notify.error(data.error || 'Failed to run depreciation');
      }
    } catch {
      notify.error('Failed to run depreciation');
    } finally {
      setRunningDep(false);
    }
  }, [workItemId, cf.units_produced, fetchRunHistory]);

  const handlePostImpairment = useCallback(async () => {
    if (!impairmentValue || !impairmentReason) {
      notify.error('Impaired value and reason are required');
      return;
    }
    setPostingImpairment(true);
    try {
      const res = await fetch('/api/v1/assets/impairment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          asset_id: workItemId,
          impaired_value: parseFloat(impairmentValue),
          reason: impairmentReason,
        }),
      });
      const data = await res.json();
      if (data.success) {
        notify.success('Impairment posted successfully');
        setShowImpairment(false);
        setImpairmentValue('');
        setImpairmentReason('');
        fetchRunHistory();
      } else {
        notify.error(data.error || 'Failed to post impairment');
      }
    } catch {
      notify.error('Failed to post impairment');
    } finally {
      setPostingImpairment(false);
    }
  }, [workItemId, impairmentValue, impairmentReason, fetchRunHistory]);

  const handlePostDisposal = useCallback(async () => {
    if (!disposalDate || disposalProceeds === '') {
      notify.error('Disposal date and proceeds are required');
      return;
    }
    setPostingDisposal(true);
    try {
      const res = await fetch('/api/v1/assets/disposals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          asset_id: workItemId,
          disposal_date: disposalDate,
          disposal_proceeds: parseFloat(disposalProceeds),
          reason: disposalReason || undefined,
          post_final_depreciation: true,
        }),
      });
      const data = await res.json();
      if (data.success) {
        notify.success('Asset disposal posted successfully');
        setShowDisposal(false);
        fetchRunHistory();
      } else {
        notify.error(data.error || 'Failed to post disposal');
      }
    } catch {
      notify.error('Failed to post disposal');
    } finally {
      setPostingDisposal(false);
    }
  }, [workItemId, disposalDate, disposalProceeds, disposalReason, fetchRunHistory]);

  const handleFieldUpdate = useCallback((key: keyof AssetCustomFields, value: unknown) => {
    const currentCf = (customFields && typeof customFields === 'object' ? customFields : {}) as Record<string, unknown>;
    onCustomFieldChange(key, value);
    // Also update the merged custom_fields object so parent can persist
    void currentCf;
  }, [customFields, onCustomFieldChange]);

  const inputClass = `w-full px-3 py-2 border rounded-lg text-sm ${
    isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
  }`;
  const labelClass = `block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`;
  const sectionClass = `p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`;
  const sectionTitleClass = `text-sm font-semibold mb-3 ${isDark ? 'text-gray-200' : 'text-gray-900'}`;
  const valueClass = `text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`;
  const mutedClass = `text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`;

  return (
    <div className="space-y-6">
      {/* Valuation Summary */}
      {purchasePrice > 0 && (
        <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <div className={`flex items-center gap-1.5 text-sm font-medium mb-1 ${mutedClass}`}>
                <DollarSign className="h-3.5 w-3.5" />
                Purchase Price
              </div>
              <div className={`text-xl font-bold ${valueClass}`}>
                {formatAssetCurrency(purchasePrice, currency)}
              </div>
              {purchaseDate && (
                <div className={`text-xs mt-0.5 ${mutedClass}`}>{formatAssetDate(purchaseDate)}</div>
              )}
            </div>
            <div>
              <div className={`flex items-center gap-1.5 text-sm font-medium mb-1 ${mutedClass}`}>
                <TrendingDown className="h-3.5 w-3.5" />
                Current Book Value
              </div>
              <div className="text-xl font-bold text-green-700 dark:text-green-400">
                {formatAssetCurrency(depreciation ? depreciation.currentValue : purchasePrice, currency)}
              </div>
              {depreciation && (
                <div className={`text-xs mt-0.5 ${mutedClass}`}>
                  {depreciation.percentDepreciated}% depreciated
                </div>
              )}
            </div>
            <div>
              <div className={`flex items-center gap-1.5 text-sm font-medium mb-1 ${mutedClass}`}>
                <AlertTriangle className="h-3.5 w-3.5" />
                Total Depreciation
              </div>
              <div className="text-xl font-bold text-orange-600 dark:text-orange-400">
                {formatAssetCurrency(depreciation ? depreciation.totalDepreciation : 0, currency)}
              </div>
              {depreciation && depreciation.monthlyDepreciation > 0 && (
                <div className={`text-xs mt-0.5 ${mutedClass}`}>
                  {formatAssetCurrency(depreciation.monthlyDepreciation, currency)}/month
                </div>
              )}
            </div>
            <div>
              <div className={`flex items-center gap-1.5 text-sm font-medium mb-1 ${mutedClass}`}>
                <Calendar className="h-3.5 w-3.5" />
                Remaining Life
              </div>
              <div className={`text-xl font-bold ${valueClass}`}>
                {depreciation ? `${depreciation.remainingMonths} mo` : '—'}
              </div>
              {depreciation && usefulLifeMonths > 0 && (
                <div className={`text-xs mt-0.5 ${mutedClass}`}>
                  of {usefulLifeMonths} months
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Purchase Information */}
      <div className={sectionClass}>
        <h4 className={sectionTitleClass}>Purchase Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Purchase Price ({currency})</label>
            <div className={`flex items-stretch overflow-hidden rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} focus-within:border-transparent focus-within:ring-2 focus-within:ring-blue-500`}>
              <span className={`inline-flex w-12 shrink-0 items-center justify-center border-r bg-gray-50 text-sm font-medium ${isDark ? 'border-gray-600 bg-gray-800/70 text-gray-300' : 'border-gray-200 text-gray-500'}`}>
                {getAssetCurrencySymbol(currency)}
              </span>
              <input
                type="number"
                step="0.01"
                value={purchasePrice || ''}
                onChange={(e) => handleFieldUpdate('purchase_price', e.target.value ? parseFloat(e.target.value) : null)}
                className={`w-full min-w-0 border-0 bg-transparent px-4 py-2 text-right font-mono ${isDark ? 'text-white' : 'text-gray-900'} placeholder:text-gray-400 focus:outline-none focus:ring-0`}
                placeholder="0.00"
              />
            </div>
          </div>
          <div>
            <label className={labelClass}>Currency</label>
            <select
              value={currency}
              onChange={(e) => handleFieldUpdate('currency', e.target.value)}
              className={inputClass}
            >
              <option value="ZAR">ZAR - Rand</option>
              <option value="USD">USD - Dollar</option>
              <option value="EUR">EUR - Euro</option>
              <option value="GBP">GBP - Pound</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Purchase Date</label>
            <input
              type="date"
              value={purchaseDate}
              onChange={(e) => handleFieldUpdate('purchase_date', e.target.value || null)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Supplier</label>
            <input
              type="text"
              value={supplier}
              onChange={(e) => handleFieldUpdate('supplier', e.target.value || null)}
              className={inputClass}
              placeholder="Supplier name"
            />
          </div>
          <div>
            <label className={labelClass}>Warranty Expiry</label>
            <input
              type="date"
              value={warrantyExpiry}
              onChange={(e) => handleFieldUpdate('warranty_expiry_date', e.target.value || null)}
              className={inputClass}
            />
          </div>
        </div>
      </div>

      {/* Asset Register Fields (Phase 13: category, location, custodian) */}
      <div className={sectionClass}>
        <h4 className={sectionTitleClass}>Asset Classification</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>Category</label>
            <input
              type="text"
              value={cf.category || ''}
              onChange={(e) => handleFieldUpdate('category', e.target.value || null)}
              className={inputClass}
              placeholder="e.g. IT Equipment, Vehicles, Furniture"
              list="asset-category-list"
            />
            <datalist id="asset-category-list">
              <option value="IT Equipment" />
              <option value="Vehicles" />
              <option value="Furniture & Fixtures" />
              <option value="Machinery" />
              <option value="Buildings" />
              <option value="Office Equipment" />
              <option value="Tools" />
            </datalist>
          </div>
          <div>
            <label className={labelClass}>Location</label>
            <input
              type="text"
              value={cf.location || ''}
              onChange={(e) => handleFieldUpdate('location', e.target.value || null)}
              className={inputClass}
              placeholder="e.g. Head Office, Warehouse A"
            />
          </div>
          <div>
            <label className={labelClass}>Custodian</label>
            <input
              type="text"
              value={cf.custodian || ''}
              onChange={(e) => handleFieldUpdate('custodian', e.target.value || null)}
              className={inputClass}
              placeholder="Person responsible for this asset"
            />
          </div>
        </div>
      </div>

      {/* Depreciation Configuration */}
      <div className={sectionClass}>
        <h4 className={sectionTitleClass}>Depreciation Configuration</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Depreciation Method</label>
            <select
              value={depreciationMethod}
              onChange={(e) => handleFieldUpdate('depreciation_method', e.target.value as DepreciationMethod)}
              className={inputClass}
            >
              <option value="straight-line">Straight-Line</option>
              <option value="declining-balance">Declining Balance (Double)</option>
              <option value="units_of_production">Units of Production</option>
              <option value="sum_of_years_digits">Sum of Years' Digits</option>
              <option value="none">No Depreciation</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Useful Life (months)</label>
            <input
              type="number"
              step="1"
              value={usefulLifeMonths || ''}
              onChange={(e) => handleFieldUpdate('useful_life_months', e.target.value ? parseInt(e.target.value) : null)}
              className={inputClass}
              placeholder="e.g. 60"
              disabled={depreciationMethod === 'none'}
            />
          </div>
          <div>
            <label className={labelClass}>Salvage / Residual Value ({currency})</label>
            <input
              type="number"
              step="0.01"
              value={salvageValue || ''}
              onChange={(e) => handleFieldUpdate('salvage_value', e.target.value ? parseFloat(e.target.value) : null)}
              className={inputClass}
              placeholder="0.00"
              disabled={depreciationMethod === 'none'}
            />
          </div>
        </div>

        {/* Units of Production inputs */}
        {depreciationMethod === 'units_of_production' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className={labelClass}>Units Produced (this period)</label>
              <input
                type="number"
                step="1"
                value={cf.units_produced ?? ''}
                onChange={(e) => handleFieldUpdate('units_produced', e.target.value ? parseFloat(e.target.value) : null)}
                className={inputClass}
                placeholder="e.g. 5000"
              />
            </div>
            <div>
              <label className={labelClass}>Total Estimated Units (lifespan)</label>
              <input
                type="number"
                step="1"
                value={cf.total_estimated_units ?? ''}
                onChange={(e) => handleFieldUpdate('total_estimated_units', e.target.value ? parseFloat(e.target.value) : null)}
                className={inputClass}
                placeholder="e.g. 100000"
              />
            </div>
          </div>
        )}

        {/* Depreciation Schedule Preview */}
        {depreciation && depreciationMethod !== 'none' && (
          <div className={`mt-4 p-3 rounded-lg border ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-white'}`}>
            <h5 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${mutedClass}`}>
              Depreciation Schedule
            </h5>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <span className={mutedClass}>Monthly Depreciation</span>
                <div className={`font-mono font-medium ${valueClass}`}>
                  {formatAssetCurrency(depreciation.monthlyDepreciation, currency)}
                </div>
              </div>
              <div>
                <span className={mutedClass}>Total Depreciated</span>
                <div className="font-mono font-medium text-orange-600 dark:text-orange-400">
                  {formatAssetCurrency(depreciation.totalDepreciation, currency)}
                </div>
              </div>
              <div>
                <span className={mutedClass}>Current Book Value</span>
                <div className="font-mono font-medium text-green-700 dark:text-green-400">
                  {formatAssetCurrency(depreciation.currentValue, currency)}
                </div>
              </div>
              <div>
                <span className={mutedClass}>Percent Depreciated</span>
                <div className={`font-mono font-medium ${valueClass}`}>
                  {depreciation.percentDepreciated}%
                </div>
              </div>
            </div>
          </div>
        )}

        {depreciationMethod === 'none' && (
          <div className={`mt-4 p-3 rounded-lg text-sm ${isDark ? 'bg-gray-900/50 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
            Depreciation tracking is disabled for this asset. The book value equals the purchase price.
          </div>
        )}

        {!depreciation && depreciationMethod !== 'none' && purchasePrice > 0 && (
          <div className={`mt-4 p-3 rounded-lg text-sm ${isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-50 text-amber-700'}`}>
            Complete the purchase date and useful life to calculate depreciation.
          </div>
        )}

        {/* Phase 13: Year-by-year depreciation schedule table */}
        {schedule && schedule.years.length > 0 && (
          <div className={`mt-4 p-3 rounded-lg border ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-white'}`}>
            <div className="flex items-center justify-between mb-2">
              <h5 className={`text-xs font-semibold uppercase tracking-wider ${mutedClass}`}>
                Year-by-Year Depreciation Schedule
              </h5>
              <button
                onClick={() => setShowSchedule(!showSchedule)}
                className={`text-xs font-medium ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
              >
                {showSchedule ? 'Hide' : 'Show'} Schedule
              </button>
            </div>
            {showSchedule && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className={isDark ? 'text-gray-400 border-b border-gray-700' : 'text-gray-600 border-b border-gray-200'}>
                      <th className="text-left py-1.5 px-2 font-medium">Year</th>
                      <th className="text-left py-1.5 px-2 font-medium">Period</th>
                      <th className="text-right py-1.5 px-2 font-medium">Beginning BV</th>
                      <th className="text-right py-1.5 px-2 font-medium">Annual Dep.</th>
                      <th className="text-right py-1.5 px-2 font-medium">Accum. Dep.</th>
                      <th className="text-right py-1.5 px-2 font-medium">Ending BV</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.years.map((yr) => (
                      <tr key={yr.year} className={isDark ? 'border-b border-gray-800' : 'border-b border-gray-100'}>
                        <td className="py-1.5 px-2 font-medium">{yr.year}</td>
                        <td className="py-1.5 px-2 text-gray-500">{yr.fiscal_year_start} → {yr.fiscal_year_end}</td>
                        <td className="py-1.5 px-2 text-right font-mono">{formatAssetCurrency(yr.beginning_book_value, currency)}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-orange-600 dark:text-orange-400">{formatAssetCurrency(yr.annual_depreciation, currency)}</td>
                        <td className="py-1.5 px-2 text-right font-mono">{formatAssetCurrency(yr.accumulated_depreciation, currency)}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-green-700 dark:text-green-400">{formatAssetCurrency(yr.ending_book_value, currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Tax vs Book comparison */}
            {taxComparison.tax && taxComparison.book && taxComparison.tax !== taxComparison.book && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-2">
                  <h5 className={`text-xs font-semibold uppercase tracking-wider ${mutedClass}`}>
                    Tax vs Book Comparison
                  </h5>
                  <button
                    onClick={() => setShowTaxConfig(!showTaxConfig)}
                    className={`text-xs font-medium ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                  >
                    {showTaxConfig ? 'Hide' : 'Configure'} Tax Settings
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className={isDark ? 'text-gray-400 border-b border-gray-700' : 'text-gray-600 border-b border-gray-200'}>
                        <th className="text-left py-1.5 px-2 font-medium">Year</th>
                        <th className="text-right py-1.5 px-2 font-medium">Book Dep.</th>
                        <th className="text-right py-1.5 px-2 font-medium">Tax Dep.</th>
                        <th className="text-right py-1.5 px-2 font-medium">Difference</th>
                        <th className="text-right py-1.5 px-2 font-medium">Book Ending</th>
                        <th className="text-right py-1.5 px-2 font-medium">Tax Ending</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taxComparison.comparison.map((row) => (
                        <tr key={row.year} className={isDark ? 'border-b border-gray-800' : 'border-b border-gray-100'}>
                          <td className="py-1.5 px-2 font-medium">{row.year}</td>
                          <td className="py-1.5 px-2 text-right font-mono">{formatAssetCurrency(row.book_depreciation, currency)}</td>
                          <td className="py-1.5 px-2 text-right font-mono">{formatAssetCurrency(row.tax_depreciation, currency)}</td>
                          <td className={`py-1.5 px-2 text-right font-mono ${row.difference > 0 ? 'text-green-600 dark:text-green-400' : row.difference < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>
                            {row.difference > 0 ? '+' : ''}{formatAssetCurrency(row.difference, currency)}
                          </td>
                          <td className="py-1.5 px-2 text-right font-mono">{row.book_ending_value != null ? formatAssetCurrency(row.book_ending_value, currency) : '—'}</td>
                          <td className="py-1.5 px-2 text-right font-mono">{row.tax_ending_value != null ? formatAssetCurrency(row.tax_ending_value, currency) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Tax depreciation configuration */}
            {showTaxConfig && (
              <div className={`mt-3 p-3 rounded-lg border ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
                <h5 className={`text-xs font-semibold mb-2 ${mutedClass}`}>Tax Depreciation Settings</h5>
                <p className={`text-xs mb-3 ${mutedClass}`}>Configure separate depreciation method/rates for tax purposes. If not set, tax mirrors book depreciation.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className={labelClass}>Tax Method</label>
                    <select
                      value={cf.tax_depreciation_method || ''}
                      onChange={(e) => handleFieldUpdate('tax_depreciation_method', e.target.value || undefined)}
                      className={inputClass}
                    >
                      <option value="">Same as book</option>
                      <option value="straight-line">Straight-Line</option>
                      <option value="declining-balance">Declining Balance</option>
                      <option value="sum_of_years_digits">Sum of Years' Digits</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Tax Useful Life (months)</label>
                    <input
                      type="number"
                      step="1"
                      value={cf.tax_useful_life_months ?? ''}
                      onChange={(e) => handleFieldUpdate('tax_useful_life_months', e.target.value ? parseInt(e.target.value) : undefined)}
                      className={inputClass}
                      placeholder="Same as book"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Tax Salvage Value ({currency})</label>
                    <input
                      type="number"
                      step="0.01"
                      value={cf.tax_salvage_value ?? ''}
                      onChange={(e) => handleFieldUpdate('tax_salvage_value', e.target.value ? parseFloat(e.target.value) : undefined)}
                      className={inputClass}
                      placeholder="Same as book"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Maintenance Cost (read-only summary — managed by Maintenance tab) */}
      {toAssetNumber(cf.total_maintenance_cost) > 0 && (
        <div className={sectionClass}>
          <h4 className={sectionTitleClass}>Maintenance Cost Summary</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className={mutedClass}>Total Maintenance Cost</span>
              <div className={`text-lg font-bold ${valueClass}`}>
                {formatAssetCurrency(toAssetNumber(cf.total_maintenance_cost), currency)}
              </div>
            </div>
            {cf.annual_maintenance_budget && toAssetNumber(cf.annual_maintenance_budget) > 0 && (
              <div>
                <span className={mutedClass}>Annual Maintenance Budget</span>
                <div className={`text-lg font-bold ${valueClass}`}>
                  {formatAssetCurrency(toAssetNumber(cf.annual_maintenance_budget), currency)}
                </div>
              </div>
            )}
          </div>
          <p className={`text-xs mt-2 ${mutedClass}`}>
            Maintenance costs are tracked via the Maintenance tab. Use that tab to log preventive,
            corrective, and compliance maintenance records.
          </p>
        </div>
      )}

      {isSaving && (
        <div className={`flex items-center gap-2 text-sm ${mutedClass}`}>
          <Save className="h-4 w-4 animate-pulse" />
          Saving changes...
        </div>
      )}

      {/* Phase 6: Server-side depreciation actions + run history */}
      {purchasePrice > 0 && depreciationMethod !== 'none' && (
        <div className={sectionClass}>
          <h4 className={sectionTitleClass}>Depreciation Runs (Server-Posted)</h4>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={handleRunDepreciation}
              disabled={runningDep}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${
                isDark ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
              } disabled:opacity-50`}
            >
              <Play className="h-3.5 w-3.5" />
              {runningDep ? 'Running...' : 'Run Depreciation'}
            </button>
            <button
              onClick={() => setShowImpairment(!showImpairment)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${
                isDark ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-amber-600 hover:bg-amber-700 text-white'
              }`}
            >
              <AlertCircle className="h-3.5 w-3.5" />
              Record Impairment
            </button>
            <button
              onClick={() => setShowDisposal(!showDisposal)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${
                isDark ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Dispose Asset
            </button>
          </div>

          {/* Impairment form */}
          {showImpairment && (
            <div className={`mb-4 p-3 rounded-lg border ${isDark ? 'border-amber-700 bg-amber-900/20' : 'border-amber-300 bg-amber-50'}`}>
              <h5 className={`text-sm font-semibold mb-2 ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>Record Impairment Write-Down</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Impaired Book Value ({currency})</label>
                  <input
                    type="number"
                    step="0.01"
                    value={impairmentValue}
                    onChange={(e) => setImpairmentValue(e.target.value)}
                    className={inputClass}
                    placeholder={String(depreciation?.currentValue ?? purchasePrice)}
                  />
                </div>
                <div>
                  <label className={labelClass}>Reason</label>
                  <input
                    type="text"
                    value={impairmentReason}
                    onChange={(e) => setImpairmentReason(e.target.value)}
                    className={inputClass}
                    placeholder="e.g. Market decline, physical damage"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={handlePostImpairment}
                  disabled={postingImpairment}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
                >
                  {postingImpairment ? 'Posting...' : 'Post Impairment'}
                </button>
                <button
                  onClick={() => setShowImpairment(false)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Disposal form */}
          {showDisposal && (
            <div className={`mb-4 p-3 rounded-lg border ${isDark ? 'border-red-700 bg-red-900/20' : 'border-red-300 bg-red-50'}`}>
              <h5 className={`text-sm font-semibold mb-2 ${isDark ? 'text-red-300' : 'text-red-800'}`}>Dispose Asset</h5>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className={labelClass}>Disposal Date</label>
                  <input
                    type="date"
                    value={disposalDate}
                    onChange={(e) => setDisposalDate(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Disposal Proceeds ({currency})</label>
                  <input
                    type="number"
                    step="0.01"
                    value={disposalProceeds}
                    onChange={(e) => setDisposalProceeds(e.target.value)}
                    className={inputClass}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className={labelClass}>Reason</label>
                  <input
                    type="text"
                    value={disposalReason}
                    onChange={(e) => setDisposalReason(e.target.value)}
                    className={inputClass}
                    placeholder="e.g. Sold, scrapped"
                  />
                </div>
              </div>
              <p className={`text-xs mt-2 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                Final depreciation will be posted up to the disposal date. The asset will be marked as disposed.
              </p>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={handlePostDisposal}
                  disabled={postingDisposal}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                >
                  {postingDisposal ? 'Posting...' : 'Confirm Disposal'}
                </button>
                <button
                  onClick={() => setShowDisposal(false)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Run history table */}
          {loadingHistory ? (
            <div className={`text-sm ${mutedClass}`}>Loading run history...</div>
          ) : runHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={isDark ? 'text-gray-400 border-b border-gray-700' : 'text-gray-600 border-b border-gray-200'}>
                    <th className="text-left py-2 px-2 font-medium">Date</th>
                    <th className="text-right py-2 px-2 font-medium">Amount</th>
                    <th className="text-right py-2 px-2 font-medium">Accum. Dep.</th>
                    <th className="text-right py-2 px-2 font-medium">Book Value</th>
                    <th className="text-left py-2 px-2 font-medium">Method</th>
                    <th className="text-left py-2 px-2 font-medium">JE</th>
                  </tr>
                </thead>
                <tbody>
                  {runHistory.map((run) => (
                    <tr key={run.id} className={isDark ? 'border-b border-gray-800' : 'border-b border-gray-100'}>
                      <td className="py-2 px-2">{formatAssetDate(run.run_date)}</td>
                      <td className="py-2 px-2 text-right font-mono">{formatAssetCurrency(Number(run.depreciation_amount), currency)}</td>
                      <td className="py-2 px-2 text-right font-mono">{formatAssetCurrency(Number(run.accumulated_depreciation), currency)}</td>
                      <td className="py-2 px-2 text-right font-mono">{formatAssetCurrency(Number(run.book_value_after), currency)}</td>
                      <td className="py-2 px-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${run.method === 'impairment' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' : isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                          {run.method.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="py-2 px-2">
                        {run.journal_entry_id ? (
                          <a
                            href={`/finance/journal?entry=${run.journal_entry_id}`}
                            className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            <FileText className="h-3 w-3" />
                            #{run.journal_entry_id}
                          </a>
                        ) : (
                          <span className={mutedClass}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={`text-sm ${mutedClass}`}>
              No depreciation runs recorded yet. Click "Run Depreciation" to post the first run.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
